import React, { useEffect, useState } from "react"
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Badge,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback
} from "reactstrap"
import { connect } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import * as Yup from "yup"
import { useFormik } from "formik"

//Import Actions
import {
    getTickets,
    createSupportTicket,
    setBreadcrumbItems
} from "../../store/actions"

const SupportTickets = props => {
    document.title = "Support Technique | CRM Entreprise"
    const navigate = useNavigate()

    const [modal, setModal] = useState(false)

    const breadcrumbItems = [
        { title: "CRM", link: "#" },
        { title: "Support", link: "#" },
        { title: "Mes Tickets", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Support Technique", breadcrumbItems)
        props.getTickets()
    }, [])

    const toggle = () => setModal(!modal)

    const [selectedFiles, setSelectedFiles] = useState([])

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            subject: "",
            category: "Technique",
            priority: "normal",
            description: ""
        },
        validationSchema: Yup.object({
            subject: Yup.string().required("Veuillez entrer un sujet"),
            description: Yup.string().required("Veuillez décrire votre problème"),
        }),
        onSubmit: (values) => {
            const formData = new FormData()
            formData.append("subject", values.subject)
            formData.append("category", values.category)
            formData.append("priority", values.priority)
            formData.append("description", values.description)

            for (let i = 0; i < selectedFiles.length; i++) {
                formData.append("attachments[]", selectedFiles[i])
            }

            props.createSupportTicket(formData, navigate)
            toggle()
        },
    })

    const getPriorityBadge = (priority) => {
        switch (priority) {
            case 'urgent': return 'danger';
            case 'high': return 'warning';
            case 'normal': return 'info';
            case 'low': return 'secondary';
            default: return 'primary';
        }
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'open': return 'success';
            case 'pending': return 'warning';
            case 'resolved': return 'primary';
            case 'closed': return 'secondary';
            default: return 'light';
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody>
                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h4 className="card-title mb-0">Mes Tickets de Support</h4>
                                        <Button color="primary" onClick={toggle}>
                                            <i className="mdi mdi-plus me-1"></i> Nouveau Ticket
                                        </Button>
                                    </div>

                                    <div className="table-responsive">
                                        <Table className="table-centered table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Sujet</th>
                                                    <th>Catégorie</th>
                                                    <th>Priorité</th>
                                                    <th>Statut</th>
                                                    <th>Dernière mise à jour</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {props.tickets && props.tickets.data && props.tickets.data.map((ticket, key) => (
                                                    <tr key={key}>
                                                        <td><Link to={`/support/tickets/${ticket.id}`} className="text-body fw-bold">#TK-{ticket.id}</Link></td>
                                                        <td>{ticket.subject}</td>
                                                        <td>{ticket.category}</td>
                                                        <td>
                                                            <Badge color={getPriorityBadge(ticket.priority)}>
                                                                {ticket.priority.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge color={getStatusBadge(ticket.status)}>
                                                                {ticket.status.toUpperCase()}
                                                            </Badge>
                                                        </td>
                                                        <td>{new Date(ticket.updated_at).toLocaleDateString()}</td>
                                                        <td>
                                                            <Link to={`/support/tickets/${ticket.id}`} className="btn btn-primary btn-sm">
                                                                Voir la discussion
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {(!props.tickets || !props.tickets.data || props.tickets.data.length === 0) && (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">Aucun ticket trouvé.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            <Modal isOpen={modal} toggle={toggle} centered>
                <ModalHeader toggle={toggle}>Ouvrir un Nouveau Ticket</ModalHeader>
                <ModalBody>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                    }}>
                        <Row>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <Label>Sujet</Label>
                                    <Input
                                        name="subject"
                                        type="text"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.subject || ""}
                                        invalid={validation.touched.subject && validation.errors.subject ? true : false}
                                        placeholder="Ex: Problème de connexion, Erreur de facturation..."
                                    />
                                    <FormFeedback type="invalid">{validation.errors.subject}</FormFeedback>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Catégorie</Label>
                                    <Input
                                        type="select"
                                        name="category"
                                        onChange={validation.handleChange}
                                        value={validation.values.category}
                                    >
                                        <option>Technique</option>
                                        <option>Facturation</option>
                                        <option>Commercial</option>
                                        <option>Autre</option>
                                    </Input>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-3">
                                    <Label>Priorité</Label>
                                    <Input
                                        type="select"
                                        name="priority"
                                        onChange={validation.handleChange}
                                        value={validation.values.priority}
                                    >
                                        <option value="low">Faible</option>
                                        <option value="normal">Normale</option>
                                        <option value="high">Haute</option>
                                        <option value="urgent">Urgente</option>
                                    </Input>
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <Label>Description détaillée du problème</Label>
                                    <Input
                                        name="description"
                                        type="textarea"
                                        rows="5"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        value={validation.values.description || ""}
                                        invalid={validation.touched.description && validation.errors.description ? true : false}
                                        placeholder="Veuillez donner le plus de détails possible..."
                                    />
                                    <FormFeedback type="invalid">{validation.errors.description}</FormFeedback>
                                </div>
                            </Col>
                            <Col lg={12}>
                                <div className="mb-3">
                                    <Label>Pièces jointes (Max 5Mo)</Label>
                                    <Input
                                        type="file"
                                        multiple
                                        className="form-control"
                                        onChange={(e) => setSelectedFiles(e.target.files)}
                                    />
                                </div>
                            </Col>
                        </Row>
                        <div className="text-end mt-3">
                            <Button type="button" color="secondary" className="me-2" onClick={toggle}>Annuler</Button>
                            <Button type="submit" color="primary">Envoyer le Ticket</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    tickets: state.Tickets.tickets,
})

export default connect(mapStateToProps, {
    getTickets,
    createSupportTicket,
    setBreadcrumbItems
})(SupportTickets)
